import React from 'react';
import styled from 'styled-components';
import shopSelectors from 'services/shop/selectors';
import { IPrintCheckHistoryItem } from 'features/report/services/types/check';
import { formatPhone } from 'utils/formatPhone';
import { ColumnsType } from 'antd/es/table';
import Table from 'components/Table';
import { formatCurrency } from 'utils/formatCurrency';

type Props = {
  value?: IPrintCheckHistoryItem[] | null;
  printRef?: React.RefObject<HTMLDivElement>;
  className?: string;
};

const ReceiptCPAPrinter = ({ value: data, printRef, className }: Props) => {
  const shopInfo = shopSelectors.data.shopInfo();
  const columns: ColumnsType<IPrintCheckHistoryItem> = [
    {
      title: 'NO.',
      dataIndex: 'checkNo',
      key: 'checkNo',
      align: 'left',
      render: (text: string) => (text?.trim() ? text : '--'),
    },
    {
      title: 'STAFF NAME',
      dataIndex: 'staffName',
      key: 'staffName',
      align: 'left',
    },
    {
      title: 'DATE & TIME',
      dataIndex: 'createdDate',
      key: 'createdDate',
      align: 'left',
      render: (text: string) => (text?.trim() ? text : '--'),
    },
    {
      title: 'AMOUNT',
      dataIndex: 'amount',
      key: 'amount',
      align: 'left',
      render: (text: number) => (text ? formatCurrency(text) : '--'),
    },
    {
      title: 'FOR',
      dataIndex: 'forNote',
      key: 'forNote',
      align: 'left',
      render: (text: string) => (text?.trim() ? text : '--'),
    },
  ];

  return (
    <Container ref={printRef} className={className}>
      <Header>
        <div className='salon-info'>
          <p className='salon-name'>{shopInfo?.shopName || '--'}</p>
          <p className='salon-address'>{shopInfo?.address || '--'}</p>
          <p className='salon-address'>
            {[shopInfo?.city, shopInfo?.state, shopInfo?.zip].filter(Boolean).join(', ') || '--'}
          </p>
          <p className='salon-address'>{formatPhone(shopInfo?.phone || '')}</p>
        </div>
      </Header>

      <TableWrap>
        <Table columns={columns} dataSource={data || []} rowKey='id' pagination={{
          pageSize: 1000,
          hideOnSinglePage: true,
        }} />
      </TableWrap>
    </Container>
  );
};

export default ReceiptCPAPrinter;

const Container = styled.div`
  width: 100%;
  padding: 16px 24px;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #fff;
`;

const Header = styled.div`
  width: 100%;

  .salon-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    width: 240px;
  }

  .salon-name {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    line-height: 18px;
    color: #000;
  }

  .salon-address {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    color: #000;
  }

  @media print {
    margin-top: 0;
  }
`;

const TableWrap = styled.div`
  width: 100%;

  .ant-table-wrapper,
  .ant-table,
  .ant-table-container,
  .ant-table-content {
    width: 100%;
    overflow: visible !important;
    border: none !important;
  }

  .ant-table table {
    width: 100%;
    border-collapse: collapse;
  }

  .ant-table-thead > tr > th {
    background: #6fabb6 !important;
    color: #fff !important;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    padding: 8px;
    height: 48px;

    border-bottom: 1px solid #ccd4dc !important;
    position: static !important;
  }

  .ant-table-thead > tr > th:first-child {
    border-top-left-radius: 8px;
  }

  .ant-table-thead > tr > th:last-child {
    border-top-right-radius: 8px;
  }

  .ant-table-thead > tr > th::before {
    display: none !important;
  }

  .ant-table-tbody > tr > td {
    padding: 4px 8px;
    font-size: 14px;
    line-height: 20px;
    color: #131921 !important;
    border-bottom: 1px solid #cfd7e3 !important;
    background: #fff;
  }

  .ant-table-tbody > tr:nth-child(even) > td {
    background: #eef4ff;
  }

  .ant-table-tbody > tr {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  @media print {
    .ant-table {
      font-size: 12px;
    }

    .ant-table table thead {
      display: table-row-group !important;
    }

    .ant-table-thead > tr > th {
      height: 48px;
      padding: 8px;
      font-size: 12px;
      line-height: 16px;
    }

    .ant-table-tbody > tr > td {
      padding: 2px 8px;
      font-size: 12px;
      line-height: 18px;
      height: 40px;
    }

    .ant-table-pagination {
      display: none !important;
    }
  }
`;